/**
 * Manual entry — §8.2's fastest path in, and the most common way tasks get added.
 *
 * Title, destination, energy and the two flags on two rows; everything else behind "more",
 * collapsed. §8.2 wants this to be title-enter-done, and a form with nine visible inputs is
 * not that — but the fields have to be *reachable* here, because going back to edit a task
 * you just typed is the friction this is trying to remove.
 *
 * The field set is the one the editor and the inbox use, so what you learn here works there.
 *
 * `QuickAddCard` is the self-contained wrapper — its own busy/error state, its own
 * `api.createTask` call — matching every other portable card (D111 pinned this to
 * `Briefing.tsx` originally; D118 made it portable like the rest). A filed-to-inbox outcome
 * posts to `notices.ts` rather than setting local state, since the "Alerts" card that shows
 * it (`NoticeCard`) can now be on a different page entirely.
 *
 * **Offline (§8.2, D154).** When a destination is chosen and `api.createTask` fails because
 * there's no connection at all (`ApiError.isOffline`), the exact payload just built is handed
 * to `taskQueue.ts` instead of surfacing an error — the outbox holds it durably and sends it
 * the moment the connection returns. Recurrence is the one exception: it's a second API call
 * keyed on the task's id, so a task queued with Repeat enabled would need that call queued and
 * chained after the first one too, which the outbox does not do. Offline plus Repeat is
 * refused with an explanation instead of silently dropping the repeat rule.
 *
 * **The inbox-bound branch (no destination) always queues, online or not.** §8.1's location
 * badge wants a *precise* fix, which can take longer to arrive than §8.2's "fastest path in"
 * is allowed to make the reader wait — so the capture is queued and the form clears
 * immediately, exactly as the offline case above already does, and a fix (or the lack of one,
 * after `LOCATION_WAIT_MS`) is attached to the still-queued record in the background before it
 * actually sends. See `position.ts` and `taskQueue.ts`'s `releaseQueuedTaskForSend`.
 *
 * **The wait starts even earlier than that.** The title field primes a fix (`primePosition`)
 * the moment it goes from empty to non-empty — before the reader has finished the thought,
 * let alone picked a destination — so the several-second lock overlaps with typing instead of
 * starting fresh at submit. Skipped when a destination is already showing (sticky from last
 * time, `recalledDestination`), since that reader is not headed for the inbox and a fix would
 * only be thrown away. Fires exactly once per capture, not on every keystroke — see
 * `primePosition`'s own doc for why that matters more than it sounds like it should.
 *
 * **Chips (D222).** Above the title: Auto (the AI picks the bin), Task, Note, Project, Context.
 * Task is everything above. The others are one line and a field or two of their own (a note's
 * destination, a project's area, Context's scope) and never wait: Enter hands the line to the
 * outbox (`queueJustAdded`), the box clears at once, and `AddResults` (Just added, D224) says what
 * each became. With an AI the worker files it; with no AI, or the bulb off, the server makes what
 * the typed fields say. Either way the entry stays until it is acknowledged. The chip is remembered (`addBin.ts`); Auto
 * is absent where there is no AI.
 *
 * Destinations are fetched here directly rather than through `useAreasAndProjects` (what
 * `AreasCard`/`GoneQuietCard` use) — that hook asks for `status=all`, which is right for a
 * page that manages archived areas but wrong for a picker deciding where a *new* task goes.
 * `api.areas()`/`api.projects()` default to `status=active`, the same call the original
 * `loadDestinations` made.
 */

import { useCallback, useEffect, useRef, useState } from "react";
import {
  ApiError,
  api,
  wasFiledToInbox,
  type Area,
  type JustAddedBody,
  type Project,
} from "../api";
import { useAiConfigured, useAiServerEnabled } from "../aiConfigured";
import { useAddBin, type AddBin } from "../addBin";
import { useJustAdded } from "../justAdded";
import { CaptureTierPicker } from "./CaptureTierPicker";
import { AddBinChips } from "./AddBinChips";
import { AddResults } from "./AddResults";
import { ContextScopePicker } from "./ContextScopePicker";
import { scopeOf } from "../contextScope";
import { useBulbTier } from "../bulbTier";
import { postNotice } from "../notices";
import {
  queueJustAdded,
  queueTaskCreate,
  releaseQueuedTaskForSend,
} from "../taskQueue";
import { LOCATION_WAIT_MS, bestEffortPosition, primePosition } from "../position";
import { mirrorTaskCreated, provisionalTask } from "../offlineMirror";
import {
  DestinationPicker,
  DetailFields,
  EnergyPicker,
  FlagButtons,
} from "./TaskFields";
import { EMPTY_FIELDS, type TaskFieldValues } from "./taskFieldValues";
import { buildRRuleString, todayLocalDate } from "./recurrence";
import { EyeToggle } from "./EyeToggle";
import {
  isDestinationStale,
  parseDestination,
  recalledDestination,
  rememberDestination,
} from "../destination";

/** How long the "we changed this, not you" pulse stays on the destination picker. Same
 * duration as `PROMOTION_HIGHLIGHT_MS` (`useHeldTaskActions.ts`) — one consistent length
 * for "briefly point at something that changed on its own" across the app. */
const DEST_RESET_PULSE_MS = 1200;

export function QuickAddCard({
  refreshKey,
  onChange,
}: {
  /** Bumped whenever something elsewhere changed, so a newly-touched area/project shows up
   * in the destination picker without this card needing to know why. */
  refreshKey?: number;
  /** Called after creating a task, so every card relying on the same tasks (Tasks, Starred,
   * or Inbox if it went there instead) knows to refetch. */
  onChange?: () => void | Promise<void>;
}) {
  const [areas, setAreas] = useState<Area[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [addError, setAddError] = useState<string | null>(null);

  const loadDestinations = useCallback(async () => {
    // Deliberately not swallowed. An empty picker after a failed fetch is
    // indistinguishable from genuinely having no areas, and that ambiguity has already
    // cost an hour once — a 401 here rendered as "you have no areas".
    try {
      const [areaBody, projectBody] = await Promise.all([api.areas(), api.projects()]);
      setAreas(areaBody.areas);
      setProjects(projectBody.projects);
      setLoadError(null);
    } catch (err) {
      if (err instanceof ApiError && err.isAuth) return; // AuthGate takes over
      setLoadError(err instanceof Error ? err.message : String(err));
    }
  }, []);

  useEffect(() => {
    void loadDestinations();
  }, [loadDestinations, refreshKey]);

  const { tier } = useBulbTier("add");
  const aiConfigured = useAiConfigured();
  const serverAi = useAiServerEnabled();
  const { bin, setBin } = useAddBin(aiConfigured, serverAi);
  // Pressing Add leaves a receipt in Just added whatever the chip (D224); what the AI makes of it
  // lands in the lists around the card, so they are told when something new is filed.
  const { entries, track, refresh, acknowledge, actions } = useJustAdded(
    () => void onChange?.(),
  );

  const add = useCallback(
    async (title: string, fields: TaskFieldValues): Promise<boolean> => {
      setBusy(true);
      setAddError(null);
      let failed = false;
      const fail = (message: string) => {
        failed = true;
        setAddError(namedFailure(title, message));
      };
      const destination = parseDestination(fields.destination);
      const payload = {
        title,
        // The Task chip is the person saying "this is a task", so an entry with no area is
        // only routed by the AI, never reclassified (D222).
        kind: "task" as const,
        just_added: true,
        ...destination,
        important: fields.important,
        starred: fields.starred,
        due: fields.due || null,
        energy: fields.energy,
        notes: fields.notes || null,
        estimate_min: fields.estimate.trim() ? Number(fields.estimate) : null,
        // This bulb's own pick (`bulbTier.ts`), so the worker runs the entry at it — or leaves
        // an Off one alone — whatever the account's capture tier says. Absent until the account
        // tier has loaded, which the server reads as "use the account's".
        ...(tier ? { tier } : {}),
      };

      if (!destination.area_id && !destination.project_id) {
        // Inbox-bound. Queued unconditionally — online or not — because a precise GPS fix
        // (§8.1, `position.ts`) can take longer than §8.2's "fastest path in" is allowed to
        // wait, and the outbox already exists to hold a write until it can go out. This is
        // that same durability reused for "waiting on a fix" instead of "waiting on a
        // connection" — see `taskQueue.ts`'s `queueTaskCreate`/`releaseQueuedTaskForSend`.
        if (fields.recurrence.enabled) {
          // Recurrence is a second call keyed on a task's id (§10), and an inbox item has no
          // id to key it on — resolving it is what creates the task recurrence would attach
          // to. Refused rather than silently dropped, the same principle the offline case
          // below applies for a different reason: intent the owner explicitly set does not
          // just vanish.
          fail(
            "Repeat needs a task to attach to. Choose an area or project first, or add " +
              "this without Repeat for now.",
          );
          setBusy(false);
          return false;
        }
        try {
          const queued = await queueTaskCreate(payload, Date.now() + LOCATION_WAIT_MS);
          // Show it straight away, under the id the queue will send (D166) - it is in Just added
          // now in every way that matters to the reader; only the network hop and the location are
          // still pending.
          mirrorTaskCreated(provisionalTask(queued.idempotencyKey, payload));
          track({ id: queued.idempotencyKey, bin: "task", text: title });
          await onChange?.();
          postNotice(
            `"${title}" is in Just added - no area chosen, and eFss will not guess one.`,
          );
          // Deliberately not awaited: the button above re-enables the moment `finally` runs,
          // so a second capture can be typed immediately rather than waiting out this one's
          // GPS lock. Whatever this resolves to — a fix, or nothing — releases the record
          // queued above and lets it actually send.
          void bestEffortPosition().then((position) =>
            releaseQueuedTaskForSend(queued.idempotencyKey, position ?? undefined),
          );
        } catch (err) {
          fail(err instanceof Error ? err.message : String(err));
        } finally {
          setBusy(false);
        }
        return !failed;
      }

      try {
        const result = await api.createTask(payload);
        if (wasFiledToInbox(result)) {
          // Shouldn't happen with a destination chosen — kept as a fallback rather than
          // assumed away, since the alternative is a task that silently vanished into the
          // inbox with no explanation on screen.
          postNotice(
            `"${result.item.raw_text}" is in Just added - no area chosen, and eFss will not guess one.`,
          );
        } else if (fields.recurrence.enabled) {
          // Recurrence lives behind its own endpoint (§10) and needs the task's id, so it
          // is set as a follow-up call once creation has actually produced one.
          await api.setRecurrence(result.id, {
            rrule: buildRRuleString(fields.recurrence),
            recur_anchor: fields.recurrence.anchor,
            recur_start: fields.due || todayLocalDate(),
          });
        }
        await onChange?.();
        void refresh();
      } catch (err) {
        if (err instanceof ApiError && err.isAuth) {
          // AuthGate takes over.
        } else if (err instanceof ApiError && err.isOffline) {
          if (fields.recurrence.enabled) {
            // Queuing the create and dropping Repeat silently would lose intent the owner
            // explicitly set; queuing both would mean chaining a second replayed call onto
            // the first, which the outbox doesn't do (D154). Refuse loudly instead.
            fail(
              "Repeat needs a connection to set up. Try again once you're back online, " +
                "or add this without Repeat for now.",
            );
          } else {
            try {
              const queued = await queueTaskCreate(payload);
              const provisional = provisionalTask(queued.idempotencyKey, payload);
              mirrorTaskCreated(provisional);
              track({ id: queued.idempotencyKey, bin: "task", text: title });
              await onChange?.();
              postNotice(`"${title}" is queued - it'll send the moment you're back online.`);
            } catch (queueErr) {
              fail(queueErr instanceof Error ? queueErr.message : String(queueErr));
            }
          }
        } else {
          fail(err instanceof Error ? err.message : String(err));
        }
      } finally {
        setBusy(false);
      }
      return !failed;
    },
    [onChange, tier, track, refresh],
  );

  /** The other chips. Resolves once the line is safely taken: held by the outbox until the server
   * has it. The server decides what to do with it - leave it for the AI, or, with the AI off or the
   * bulb Off, make what the typed fields say - and either way the entry is the receipt (D224). */
  const addFiled = useCallback(
    async (target: AddBin, text: string, fields: ChipFields): Promise<boolean> => {
      setAddError(null);
      try {
        const body: Omit<JustAddedBody, "id"> = {
          kind: target as JustAddedBody["kind"],
          text,
          ...(tier ? { tier } : {}),
          ...(target === "note" ? parseDestination(fields.destination) : {}),
          ...(target === "project" && fields.area ? { area_id: fields.area } : {}),
          ...(target === "context" ? scopeOf(fields.scope) : {}),
        };
        const { id } = await queueJustAdded(body);
        track({ id, bin: target, text });
        await onChange?.();
        return true;
      } catch (err) {
        if (err instanceof ApiError && err.isAuth) return true; // AuthGate takes over
        setAddError(namedFailure(text, err instanceof Error ? err.message : String(err)));
        return false;
      }
    },
    [onChange, tier, track],
  );

  return (
    <QuickAdd
      onAdd={add}
      onAddFiled={addFiled}
      bin={bin}
      onBin={setBin}
      aiConfigured={aiConfigured}
      serverAi={serverAi}
      busy={busy}
      areas={areas}
      projects={projects}
      loadError={loadError}
      addError={addError}
      results={
        <AddResults
          entries={entries}
          areas={areas}
          projects={projects}
          actions={actions}
          onAcknowledge={(ids) => void acknowledge(ids)}
          onChanged={onChange}
        />
      }
    />
  );
}

/** A failed add, naming the line: the box may hold the next one by the time this shows. */
function namedFailure(text: string, message: string): string {
  return `Couldn't add "${text}". ${message}`;
}

/** What the non-task chips carry besides the line: a note's destination, a project's area,
 * Context's scope (the pickers' own strings). */
type ChipFields = { destination: string; area: string; scope: string };

const PLACEHOLDER: Record<AddBin, string> = {
  auto: "Add anything: the AI decides where it goes",
  task: "Add a task: title, enter, done",
  note: "Add a note: a title, enter, done",
  project: "Add a project: its name",
  context: "Add something the AI should know",
};

/** The two controls that end the add form, wherever it currently ends. */
function FormControls({
  showMore,
  onToggle,
  busy,
  ready,
}: {
  showMore: boolean;
  onToggle: () => void;
  busy: boolean;
  ready: boolean;
}) {
  return (
    <>
      <EyeToggle
        open={showMore}
        onToggle={onToggle}
        label="due date, notes, estimate and repeat"
      />
      <button className="btn primary small" type="submit" disabled={busy || !ready}>
        Add
      </button>
    </>
  );
}

function QuickAdd({
  onAdd,
  onAddFiled,
  bin,
  onBin,
  aiConfigured,
  serverAi,
  busy,
  areas,
  projects,
  loadError,
  addError,
  results,
}: {
  /** Each resolves false when the line was not taken, so the box can give it back. */
  onAdd: (title: string, values: TaskFieldValues) => Promise<boolean>;
  /** Every chip but Task. */
  onAddFiled: (bin: AddBin, text: string, fields: ChipFields) => Promise<boolean>;
  bin: AddBin;
  onBin: (bin: AddBin) => void;
  aiConfigured: boolean;
  serverAi: boolean;
  busy: boolean;
  areas: Area[];
  projects: Project[];
  loadError: string | null;
  /** A failed create, distinct from `loadError` — the picker loaded fine, the submit did not. */
  addError: string | null;
  /** The "Just added" list, which lives inside the card under the form. */
  results: React.ReactNode;
}) {
  const [title, setTitle] = useState("");
  const [values, setValues] = useState<TaskFieldValues>(() => ({
    ...EMPTY_FIELDS,
    destination: recalledDestination(),
  }));
  const [showMore, setShowMore] = useState(false);
  // The Project chip's area and Context's scope: their own, so switching chip and back keeps
  // what was picked, and neither disturbs the destination Task and Note share.
  const [area, setArea] = useState("");
  const [scope, setScope] = useState("");
  const [destResetPulse, setDestResetPulse] = useState(false);
  const pulseTimer = useRef<number | undefined>(undefined);
  const input = useRef<HTMLInputElement>(null);

  const update = (patch: Partial<TaskFieldValues>) =>
    setValues((current) => ({ ...current, ...patch }));

  // If the remembered destination has since been archived or renamed away, fall back to
  // the inbox rather than silently filing into something else.
  const known = new Set([
    ...areas.map((a) => `area:${a.id}`),
    ...projects.map((p) => `project:${p.id}`),
  ]);
  const destination = known.has(values.destination) ? values.destination : "";

  /**
   * Snap the picker back to inbox if the remembered destination has gone stale
   * (`destination.ts`), and say so with a brief pulse rather than changing it silently —
   * reported directly: the reader needs to be able to tell "we did this" from "I meant to
   * pick inbox." Called at two points, deliberately not on a timer: focus of the title
   * field (so the picker is already telling the truth before a reader even glances at it)
   * and, as the actual authoritative gate, right at submit — the one case focus alone
   * misses is a title typed well within the hour, then `Add` clicked only after it, with
   * nothing re-focusing the field in between.
   */
  function reclaimIfStale(): string {
    if (!destination || !isDestinationStale()) return destination;
    update({ destination: "" });
    window.clearTimeout(pulseTimer.current);
    setDestResetPulse(true);
    pulseTimer.current = window.setTimeout(() => setDestResetPulse(false), DEST_RESET_PULSE_MS);
    return "";
  }

  async function submit(event: React.FormEvent) {
    event.preventDefault();
    const text = title.trim();
    if (!text) return;
    setTitle("");
    // The authoritative check: whatever the picker showed a moment ago, this is what
    // actually gets used — never the possibly-stale value the form's been holding since
    // an earlier render.
    const effective = reclaimIfStale();
    rememberDestination(effective);
    // The box clears at once (the outbox has the line); a line that was not taken comes back,
    // unless the next one is already being typed, when the error names it instead.
    const giveBack = () => setTitle((current) => (current === "" ? text : current));
    if (bin !== "task") {
      if (!(await onAddFiled(bin, text, { destination: effective, area, scope }))) giveBack();
      input.current?.focus();
      return;
    }
    if (await onAdd(text, { ...values, destination: effective })) {
      setValues({ ...EMPTY_FIELDS, destination: effective });
      setShowMore(false);
    } else {
      giveBack();
    }
    input.current?.focus();
  }

  return (
    <section className="panel">
      <form onSubmit={submit} style={{ display: "grid", gap: "0.55rem" }}>
        <AddBinChips
          bin={bin}
          onChange={(next) => {
            onBin(next);
            input.current?.focus();
          }}
          aiConfigured={aiConfigured}
          serverAi={serverAi}
        />

        <input
          ref={input}
          className="input"
          placeholder={PLACEHOLDER[bin]}
          aria-label={bin === "task" ? "New task title" : `New ${bin} text`}
          value={title}
          onChange={(e) => {
            const next = e.target.value;
            // Fires once, on the empty-to-non-empty edge only — not "while destination is
            // still Inbox", checked fresh on every keystroke, which would ask again on every
            // character if a dismissed (not denied) permission prompt left the browser still
            // askable. `primePosition` is its own second guard underneath this one, so even a
            // stray extra call here costs nothing.
            if (bin === "task" && title === "" && next !== "" && !destination) primePosition();
            setTitle(next);
          }}
          onFocus={reclaimIfStale}
        />

        {bin === "task" ? (
          <div className="row wrap">
            <DestinationPicker
              value={destination}
              areas={areas}
              projects={projects}
              onChange={(next) => {
                update({ destination: next });
                rememberDestination(next);
              }}
              fullWidth
              allowInbox
              resetPulse={destResetPulse}
            />

            <EnergyPicker value={values.energy} onChange={(energy) => update({ energy })} />

            <FlagButtons
              important={values.important}
              starred={values.starred}
              onImportant={(important) => update({ important })}
              onStarred={(starred) => update({ starred })}
            />

            <CaptureTierPicker surface="add" />

            <span style={{ flex: 1 }} />

            {/*
              Collapsed, the controls end the only row there is. Expanded, they move below the
              extra fields - so the confirm button is always the last thing on the form, and
              always where Cancel and Save sit in the editor.
            */}
            {!showMore && (
              <FormControls
                showMore={showMore}
                onToggle={() => setShowMore((v) => !v)}
                busy={busy}
                ready={Boolean(title.trim())}
              />
            )}
          </div>
        ) : (
          <div className="row wrap">
            {bin === "note" && (
              <DestinationPicker
                value={destination}
                areas={areas}
                projects={projects}
                onChange={(next) => {
                  update({ destination: next });
                  rememberDestination(next);
                }}
                fullWidth
                allowInbox
                resetPulse={destResetPulse}
              />
            )}
            {bin === "project" && (
              <select
                className="input"
                style={{ flex: "1 1 100%", minWidth: 0 }}
                aria-label="Area"
                value={area}
                onChange={(e) => setArea(e.target.value)}
              >
                <option value="">{aiConfigured ? "Let the AI choose the area" : "Choose an area"}</option>
                {areas.map((a) => (
                  <option key={a.id} value={a.id}>
                    {a.name}
                  </option>
                ))}
              </select>
            )}
            {bin === "context" && (
              <ContextScopePicker value={scope} areas={areas} projects={projects} onChange={setScope} />
            )}
            <CaptureTierPicker surface="add" />
            <span style={{ flex: 1 }} />
            <button
              className="btn primary small"
              type="submit"
              disabled={busy || !title.trim()}
            >
              Add
            </button>
          </div>
        )}

        {bin === "task" && showMore && (
          <>
            <DetailFields values={values} onChange={update} />
            <div className="row wrap">
              <span style={{ flex: 1 }} />
              <FormControls
                showMore={showMore}
                onToggle={() => setShowMore((v) => !v)}
                busy={busy}
                ready={Boolean(title.trim())}
              />
            </div>
          </>
        )}

        {loadError && (
          <p className="small" style={{ margin: 0, color: "var(--warn)" }}>
            Could not load areas and projects ({loadError}). You can still add - it will go to
            the inbox.
          </p>
        )}

        {addError && (
          <p className="error small" style={{ margin: 0 }}>
            {addError}
          </p>
        )}
      </form>
      {results}
    </section>
  );
}
